import DOMPurify from 'dompurify'

const HTML_ENTITY_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

const CSS_VAR_NAME_PATTERN = /^--[\w-]+$/
const CSS_VALUE_UNSAFE = /<|>|\\0/

/**
 * Escape text for safe insertion into HTML template literals.
 * Prefer sanitizing the assembled markup at assignment time; use this when
 * building strings that may not pass through `sanitizeHtml`.
 */
export function escapeHtml(value) {
  if (value == null) return ''
  return String(value).replace(/[&<>"']/g, (ch) => HTML_ENTITY_MAP[ch])
}

/** DOMPurify profile for Mapbox marker/popup markup (Tailwind classes, data-*, SVG). */
export const MAP_HTML_PURIFY_CONFIG = {
  USE_PROFILES: { html: true, svg: true },
  ADD_ATTR: [
    'data-shipment-id',
    'data-shipment-uuid',
    'data-dir',
    'data-key',
    'data-index',
    'data-testid',
    'role',
    'tabindex',
    'target',
    'dominant-baseline',
    'text-anchor',
    'viewbox',
    'viewBox',
    'transform',
    'xmlns',
    'fill',
    'stroke',
    'stroke-width',
    'strokeWidth',
    'stroke-linecap',
    'strokeLinecap',
    'stroke-linejoin',
    'strokeLinejoin',
    'clip-path',
    'clipPath',
  ],
}

/**
 * @param {string} html
 * @param {import('dompurify').Config} [config]
 */
export function sanitizeHtml(html, config = MAP_HTML_PURIFY_CONFIG) {
  if (html == null || html === '') return ''
  if (typeof window === 'undefined') return String(html)
  return DOMPurify.sanitize(String(html), config)
}

/** Remove all child nodes without parsing HTML. */
export function clearElement(element) {
  if (!element) return
  element.replaceChildren()
}

function toSafeHtmlString(html, options = {}) {
  const { trusted = false, config = MAP_HTML_PURIFY_CONFIG } = options
  return trusted ? String(html ?? '') : sanitizeHtml(html, config)
}

/**
 * Parse HTML and append nodes to `element` (sanitized unless `trusted`).
 * @param {HTMLElement} element
 * @param {string} html
 * @param {{ trusted?: boolean, config?: import('dompurify').Config }} [options]
 */
function appendHtmlToElement(element, html, options = {}) {
  if (!element) return
  const safe = toSafeHtmlString(html, options)
  if (!safe) return
  const doc = new DOMParser().parseFromString(safe, 'text/html')
  for (const node of [...doc.body.childNodes]) {
    element.appendChild(node)
  }
}

/**
 * Replace element contents with sanitized HTML (no direct `innerHTML`).
 * @param {HTMLElement} element
 * @param {string} html
 * @param {{ trusted?: boolean, config?: import('dompurify').Config }} [options]
 */
export function setElementHtml(element, html, options = {}) {
  if (!element) return
  clearElement(element)
  appendHtmlToElement(element, html, options)
}

/** Safe text assignment (replaces `innerHTML` for plain text / numbers). */
export function setElementText(element, text) {
  if (!element) return
  element.textContent = text == null ? '' : String(text)
}

/**
 * Parse sanitized HTML and return the first element child (legacy donut marker pattern).
 * @param {string} html
 * @param {{ trusted?: boolean, config?: import('dompurify').Config }} [options]
 * @returns {Element | null} First parsed element node, if any.
 */
export function htmlToFirstChild(html, options = {}) {
  const wrapper = document.createElement('div')
  appendHtmlToElement(wrapper, html, options)
  return wrapper.firstElementChild
}

/**
 * Mapbox `Popup.setHTML` / `innerHTML` replacement — sanitizes, prefers `setDOMContent`.
 * @param {mapboxgl.Popup} popup
 * @param {string} html
 * @param {{ trusted?: boolean, config?: import('dompurify').Config }} [options]
 */
export function applyMapboxPopupHtml(popup, html, options = {}) {
  if (!popup) return popup
  const safe = toSafeHtmlString(html, options)
  if (typeof popup.setDOMContent === 'function') {
    const wrapper = document.createElement('div')
    appendHtmlToElement(wrapper, safe, { trusted: true })
    popup.setDOMContent(wrapper)
  } else if (typeof popup.setHTML === 'function') {
    popup.setHTML(safe)
  }
  return popup
}

/** Prefer mounting parsed root markup directly (avoids extra wrapper nodes in Mapbox popups). */
export function applyMapboxPopupRootHtml(popup, html, options = {}) {
  if (!popup) return popup
  const root = htmlToFirstChild(html, options)
  if (!root) return popup

  if (typeof popup.setDOMContent === 'function') {
    popup.setDOMContent(root)
  } else if (typeof popup.setHTML === 'function') {
    popup.setHTML(root.outerHTML)
  }
  return popup
}

export function isSafeCssVarName(name) {
  return typeof name === 'string' && CSS_VAR_NAME_PATTERN.test(name.trim())
}

export function isSafeCssValue(value) {
  if (value == null) return true
  const s = String(value).trim()
  if (!s) return true
  return !CSS_VALUE_UNSAFE.test(s)
}

/**
 * Build a `:root { … }` block from tenant design tokens (validated names/values).
 * @param {Record<string, unknown>} designTokens
 * @param {string} [fontFamily]
 */
export function buildRootCssBlock(designTokens, fontFamily) {
  const tokens = { ...(designTokens || {}) }
  tokens['--font-family'] = fontFamily || 'Nunito, sans-serif'
  const declarations = []
  for (const [key, value] of Object.entries(tokens)) {
    const k = String(key).trim()
    if (!isSafeCssVarName(k)) continue
    const v = String(value)
    if (!isSafeCssValue(v)) continue
    declarations.push(`${k}: ${v};`)
  }
  if (declarations.length === 0) return ''
  return `:root { ${declarations.join(' ')} }`
}

/** Inject CSS via `textContent` (avoids HTML parsing on `<style>`). */
export function setStyleTextContent(styleElement, cssText) {
  if (!styleElement) return
  styleElement.textContent = cssText == null ? '' : String(cssText)
}

/** Static Geofence map recenter control (trusted markup). */
export const GEOFENCE_MAP_RECENTER_CONTROL_HTML = `
            <button class="mapboxgl-ctrl-icon recenter-map-btn" type="button" title="Recenter map">
              <span class="mapboxgl-ctrl-icon" aria-hidden="true" style="display: flex; align-items: center; justify-content: center; height: 100%;">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 19L13 13M1.26801 10.043C1.70497 11.4661 2.58322 12.7134 3.77577 13.6045C4.96833 14.4956 6.41339 14.9843 7.90201 15C9.62399 15.0212 11.2936 14.4081 12.5925 13.2774C13.8914 12.1466 14.7288 10.5775 14.945 8.869C15.1578 7.16051 14.7334 5.43354 13.7531 4.01821C12.7728 2.60289 11.3052 1.59848 9.63101 1.197C7.95642 0.791936 6.19135 1.01674 4.67176 1.82863C3.15217 2.64052 1.98414 3.9828 1.39001 5.6M1 2V6H5" stroke="#33455B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          `
